/**
 * Treeline layer
 *
 * Renders the climatic treeline band as pre-computed vector contour lines from a
 * PMTiles archive (built by scripts/treeline → gdal_contour → tippecanoe). This
 * replaces the former on-the-fly DEM + COG marching-squares contouring, which
 * undersampled the elevation field when zoomed out — the band visibly drifted
 * into forested ground and showed stair-step straight lines, and the
 * viewport-relative sample lattice made the line shift on every pan/zoom.
 *
 * Vector tiles are geography-stable and range-read (only the viewport's tiles are
 * fetched), so the line is fixed and cheap at every zoom. The treeline COG
 * (treeline-cog-service) stays the source for point/grid *queries* — the View's
 * Below / Near / Above classification — while this layer is just the rendered
 * band boundaries:
 *   upper (terrain ≥ treeline + buffer) → blue  = near→above-treeline boundary
 *   lower (terrain ≤ treeline − buffer) → green = below→near boundary
 */

import maplibregl from 'maplibre-gl'
import { Protocol, PMTiles } from 'pmtiles'
import { Capacitor } from '@capacitor/core'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'

const TILE_ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''
// Versioned filename busts CloudFront for this archive (edge invalidation has
// proved unreliable here); bump on each re-deploy and keep run_pipeline.sh's
// PMTILES_S3_DEST in sync.
// NOTE: Colorado-only prototype. Two seed-and-grow land-cover lines from one
// archive (generate_treeline_region.py): the climate model seeds where to look,
// then an "alpine" region grows downhill through actual non-green Copernicus
// canopy and stops at the green edge. Two canopy thresholds bracket the ecotone:
//   kind=1 below (green) — top of SOLID forest (canopy ≥ 40%): below → not near
//   kind=2 above (blue)  — LAST trees (canopy ≥ 8%):           above → not near
// The band between them is "near treeline". Both are region boundaries →
// continuous by construction. Swap to the global build once validated.
const PMTILES_URL = `${TILE_ORIGIN}/data/treeline/treeline_co_cmp18.pmtiles`

const SOURCE_ID = 'treeline-source'
const CLIMATE_LAYER_ID = 'treeline-climate-layer' // kind=0, orange — climate seed (debug reference)
const BELOW_LAYER_ID = 'treeline-below-layer' // kind=1, green — top of solid forest (below→near)
const ABOVE_LAYER_ID = 'treeline-above-layer' // kind=2, blue — last trees (near→above)
/** tippecanoe layer name (run_pipeline.sh: `-l treeline`). */
const SOURCE_LAYER = 'treeline'
/** Tiles are built to z13 (GLO-30 is ~30 m, ≈ z12–13 native); the client
 *  overzooms past that. (Range-read → the archive's total size doesn't affect
 *  per-view fetch, so we keep full detail.) */
const TILE_MAXZOOM = 13
const DEFAULT_OPACITY = 0.9

class TreelineService {
  private map?: maplibregl.Map
  private protocol: Protocol | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY

  get enabled(): boolean {
    return this._enabled
  }
  get opacity(): number {
    return this._opacity
  }

  /** Reuses the shared `pmtiles` protocol (MapLibre allows one global handler);
   *  falls back to creating it if this is somehow the first pmtiles layer. */
  register(map: maplibregl.Map, existingProtocol?: Protocol): void {
    this.map = map
    if (existingProtocol) {
      this.protocol = existingProtocol
      this.protocol.add(new PMTiles(PMTILES_URL))
    } else {
      this.protocol = new Protocol()
      this.protocol.add(new PMTiles(PMTILES_URL))
      maplibregl.addProtocol('pmtiles', this.protocol.tile)
    }
  }

  enable(): void {
    if (!this.map || this._enabled) return
    this._enabled = true

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'vector',
        url: `pmtiles://${PMTILES_URL}`,
        maxzoom: TILE_MAXZOOM,
      })
    }
    // Climate seed (kind=0) — orange dashed, debug reference for the seed-and-grow.
    if (!this.map.getLayer(CLIMATE_LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: CLIMATE_LAYER_ID,
          type: 'line',
          source: SOURCE_ID,
          'source-layer': SOURCE_LAYER,
          filter: ['==', ['get', 'kind'], 0],
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': '#f97316',
            'line-width': 1.5,
            'line-dasharray': [1, 2],
            'line-opacity': this._opacity * 0.7,
          },
        },
        'treeline'
      )
    }
    // Below-treeline boundary (kind=1, top of solid forest) — green dashed.
    if (!this.map.getLayer(BELOW_LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: BELOW_LAYER_ID,
          type: 'line',
          source: SOURCE_ID,
          'source-layer': SOURCE_LAYER,
          filter: ['==', ['get', 'kind'], 1],
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': '#ef4444',
            'line-width': 2.5,
            'line-dasharray': [2, 2],
            'line-opacity': this._opacity,
          },
        },
        'treeline'
      )
    }
    // Above-treeline boundary (kind=2, last trees) — blue dashed.
    if (!this.map.getLayer(ABOVE_LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: ABOVE_LAYER_ID,
          type: 'line',
          source: SOURCE_ID,
          'source-layer': SOURCE_LAYER,
          filter: ['==', ['get', 'kind'], 2],
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': '#3b82f6',
            'line-width': 2.5,
            'line-dasharray': [2, 2],
            'line-opacity': this._opacity,
          },
        },
        'treeline'
      )
    }
  }

  disable(): void {
    if (!this.map) return
    this._enabled = false
    for (const id of [CLIMATE_LAYER_ID, BELOW_LAYER_ID, ABOVE_LAYER_ID]) {
      if (this.map.getLayer(id)) this.map.removeLayer(id)
    }
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID)
  }

  toggle(): void {
    if (this._enabled) this.disable()
    else this.enable()
  }

  setOpacity(o: number): void {
    this._opacity = Math.max(0, Math.min(1, o))
    if (!this.map) return
    for (const id of [CLIMATE_LAYER_ID, BELOW_LAYER_ID, ABOVE_LAYER_ID]) {
      if (this.map.getLayer(id)) this.map.setPaintProperty(id, 'line-opacity', this._opacity)
    }
  }

  /** Re-add source + layer after a base-style reload wipes them. */
  reAdd(): void {
    if (!this._enabled || !this.map) return
    this.disable()
    this._enabled = false
    this.enable()
  }
}

export const treelineService = new TreelineService()
